import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { unregisterPush } from './device';

// Same login as the Sentinel portal (apps/portal/src/contexts/AuthContext.tsx): the
// country project's Supabase Auth, the scope and super admin flag from the login hook,
// and the person's own users row for their name, permissions and POS flag.

type Scope = 'own' | 'assigned' | 'territory' | 'all' | 'global';

interface AppMetadata {
  scope?: Scope;
  is_super_admin?: boolean;
  territory_ids?: string[];
}

interface PermissionEntry {
  level?: 'off' | 'view' | 'contribute' | 'manage';
}

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  status: string | null;
  is_pos_staff: boolean | null;
  permissions: Record<string, PermissionEntry | undefined> | null;
}

export type Role = 'operator' | 'head_office';
export type Brand = 'cmb' | 'pos';

export interface ChatsAccess {
  /** May use the app at all: any portal access, or the POS team. */
  allowed: boolean;
  role: Role;
  /** Brands whose chats this person can be given. */
  brands: Brand[];
  /** The login's super admin claim: the same test the database uses for head office notes (1 Nov 2026). */
  superAdmin?: boolean;
  /** Comments and Reviews (Gary, 3 Nov 2026): head office answers; the social_inbox permission at view reads only. */
  comments?: 'off' | 'view' | 'manage';
}

interface AuthValue {
  session: Session | null;
  profile: Profile | null;
  access: ChatsAccess;
  /** True while the session or the person's profile is still loading. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  /** The profile did not load (no signal, a server error): not the same as having no access (Gary, 19 Oct). */
  profileFailed: boolean;
  retryProfile: () => void;
}

const NO_ACCESS: ChatsAccess = { allowed: false, role: 'operator', brands: [] };
const META_KEYS = new Set(['_scope', 'ambassador_portal', 'pos_portal']);

function accessFor(meta: AppMetadata | null, profile: Profile | null): ChatsAccess {
  if (!profile || profile.status !== 'active') return NO_ACCESS;
  const perms = profile.permissions ?? {};
  const superAdmin = meta?.is_super_admin === true;
  const portalUser = superAdmin || Object.entries(perms).some(([key, val]) => !META_KEYS.has(key) && !!val?.level && val.level !== 'off');
  const posTeam = profile.is_pos_staff === true || (!!perms.pos_portal?.level && perms.pos_portal.level !== 'off');
  const headOffice = superAdmin || meta?.scope === 'global';

  const brands: Brand[] = [];
  if (portalUser) brands.push('cmb');
  if (posTeam || headOffice) brands.push('pos');
  const commentsLevel = perms.social_inbox?.level;
  const comments = headOffice || commentsLevel === 'manage' || commentsLevel === 'contribute' ? 'manage' : commentsLevel === 'view' ? 'view' : 'off';
  return { allowed: portalUser || posTeam, role: headOffice ? 'head_office' : 'operator', brands, superAdmin, comments };
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [profileFailed, setProfileFailed] = useState(false);

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, status, is_pos_staff, permissions')
      .eq('id', userId)
      .single();
    if (error) console.error('Failed to load user profile:', error.message);
    // No row is "no access"; anything else (offline, a timeout) is a failed load the person can try again.
    setProfileFailed(!!error && error.code !== 'PGRST116');
    setProfile(error ? null : (data as Profile));
    setProfileFor(userId);
  }, []);

  useEffect(() => {
    void supabase.auth.getSession().then(async ({ data: { session: current } }) => {
      setSession(current);
      if (current?.user) await loadProfile(current.user.id);
      setSessionLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (next?.user) void loadProfile(next.user.id);
      else {
        setProfile(null);
        setProfileFor(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [loadProfile]);

  async function signIn(email: string, password: string): Promise<string | null> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error?.message ?? null;
  }

  async function signOut() {
    // This device stops alerting this person, so whoever signs in next gets it (review, 19 Oct).
    await unregisterPush();
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setProfileFor(null);
  }

  const retryProfile = () => { if (session?.user) void loadProfile(session.user.id); };
  const meta = (session?.user.app_metadata as AppMetadata | undefined) ?? null;
  const access = accessFor(meta, profile);
  const loading = sessionLoading || (!!session && profileFor !== session.user.id);

  // Local development only (stripped from production builds): ?preview=operator or
  // ?preview=head_office shows the signed-in app on sample data without a login.
  if (import.meta.env.DEV) {
    const preview = new URLSearchParams(window.location.search).get('preview') ?? sessionStorage.getItem('chats.preview');
    if (preview === 'operator' || preview === 'head_office') {
      sessionStorage.setItem('chats.preview', preview);
      const fakeSession = { user: { id: 'preview', email: 'preview@cleanmybed.com', app_metadata: {} } } as unknown as Session;
      const fakeProfile: Profile = { id: 'preview', first_name: 'Preview', last_name: preview === 'operator' ? 'Operator' : 'Head office', status: 'active', is_pos_staff: true, permissions: { leads: { level: 'contribute' }, customers: { level: 'contribute' }, bookings: { level: 'contribute' } } };
      const fakeAccess: ChatsAccess = { allowed: true, role: preview, brands: ['cmb', 'pos'], comments: preview === 'head_office' ? 'manage' : 'off' };
      return (
        <AuthContext.Provider value={{ session: fakeSession, profile: fakeProfile, access: fakeAccess, loading: false, signIn, signOut, profileFailed: false, retryProfile }}>
          {children}
        </AuthContext.Provider>
      );
    }
  }

  return (
    <AuthContext.Provider value={{ session, profile, access, loading, signIn, signOut, profileFailed, retryProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
